import { useEffect, useRef, useState } from "react";
import Button from "rsuite/Button";
import { FiChevronDown, FiSearch } from "react-icons/fi";
import {
  OrderListFilterOption,
  resolveAppliedSelection,
  toggleSelectAllOptions,
} from "../../helpers/orderListFilterHelper";

interface Props {
  label: string;
  options: Array<string | OrderListFilterOption>;
  selected: string[] | null;
  onApply: (selected: string[] | null) => void;
  ariaLabel: string;
}

const OrderListMultiSelectFilter = ({ label, options, selected, onApply, ariaLabel }: Props): JSX.Element => {
  const optionValue = (option: string | OrderListFilterOption): string =>
    typeof option === "string" ? option : option.value;
  const optionLabel = (option: string | OrderListFilterOption): string =>
    typeof option === "string" ? option : option.label;
  const optionValues = options.map(optionValue);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<Set<string>>(new Set(selected ?? optionValues));
  const [search, setSearch] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) {
      return;
    }
    setDraft(new Set(selected ?? optionValues));
    setSearch("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) {
      return;
    }
    const handleClickOutside = (event: MouseEvent): void => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  const normalizedSearch = search.trim().toLowerCase();
  const visibleOptions = options.filter(option => optionLabel(option).toLowerCase().includes(normalizedSearch));
  const visibleValues = visibleOptions.map(optionValue);
  const allVisibleSelected = visibleValues.length > 0 && visibleValues.every(option => draft.has(option));
  const summary = selected === null || selected.length === options.length ? "All" : `${selected.length} selected`;

  const toggleOption = (option: string): void => {
    setDraft(current => {
      const next = new Set(current);
      if (next.has(option)) {
        next.delete(option);
      } else {
        next.add(option);
      }
      return next;
    });
  };

  // Select all replaces the draft with only the currently visible (searched) options.
  const toggleSelectAll = (): void => {
    setDraft(current => toggleSelectAllOptions(current, visibleValues));
  };

  const clearDraft = (): void => {
    setDraft(new Set(optionValues));
  };

  const applyDraft = (): void => {
    onApply(resolveAppliedSelection(draft, optionValues, visibleValues, normalizedSearch !== ""));
    setOpen(false);
  };

  return (
    <div className="order-list-multiselect" ref={containerRef}>
      <button
        type="button"
        className="order-list-multiselect__trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        onClick={() => setOpen(current => !current)}
      >
        <span>{label}: <strong>{summary}</strong></span>
        <FiChevronDown aria-hidden="true" />
      </button>
      {open && <div className="order-list-multiselect__popover" role="listbox">
        <div className="order-list-multiselect__search">
          <FiSearch aria-hidden="true" />
          <input
            aria-label={`Search ${label.toLowerCase()}`}
            placeholder={`Search ${label.toLowerCase()}...`}
            value={search}
            onChange={event => setSearch(event.target.value)}
          />
        </div>
        <div className="order-list-multiselect__options">
          <label className="order-list-multiselect__option order-list-multiselect__option--all">
            <input type="checkbox" checked={allVisibleSelected} onChange={toggleSelectAll} /> Select all
          </label>
          {visibleOptions.map(option => {
            const value = optionValue(option);
            return <label className="order-list-multiselect__option" key={value}>
              <input type="checkbox" checked={draft.has(value)} onChange={() => toggleOption(value)} /> {optionLabel(option)}
            </label>;
          })}
        </div>
        <div className="order-list-multiselect__footer">
          <button type="button" className="order-list-multiselect__clear" onClick={clearDraft}>Clear</button>
          <Button appearance="primary" size="sm" onClick={applyDraft}>Apply</Button>
        </div>
      </div>}
    </div>
  );
};

export default OrderListMultiSelectFilter;
